// @flow

import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import dataComponent from '../hoc/dataComponent';
import { defineMessages } from 'react-intl';

import TitleWithPopularTitle from '../components/common/TitleWithPopularTitle';
import ComposerList from '../components/common/ComposerList';
import AuthorList from '../components/common/AuthorList';
import LiveCompareInvitationOverlay from '../components/live-compare/LiveCompareInvitationOverlay';
import CapacitorHeaderBar from '../components/capacitor/HeaderBar';

import { ApiError } from '../middleware/api';

import * as recordingActions from '../actions/recording';
import * as workActions from '../actions/work';
import { loadTopRecordingFacets } from '../actions/facet';

import {
  selectFilteredAndSortedRecordings,
  selectNextFilteredRecordingsCursor,
  selectPreviousFilteredRecordingsCursor,
} from '../selectors/recording';

import { selectWork } from '../selectors/work';

import { selectActiveRecordingFacets } from '../selectors/facet';

import { selectRecordingsFilterParams } from '../selectors/filteredList';

import {
  WORKS_PAGE_FACET_FILTER_ORDERS,
  WORKS_PAGE_FACET_FILTER_ORDER_A as DEFAULT_WORKS_PAGE_FACET_FILTER_ORDER,
  ENTITY_TYPE_WORK,
  META_TITLE_PREFIX,
} from '../constants';

import styles from './Work.css';
import FilteredRecordingList from '../components/common/FilteredRecordingList';
import * as clientActions from '../actions/client';

declare var __CAPACITOR__;

const messages = defineMessages({
  metaDescription: {
    id: 'work.meta.description',
    defaultMessage:
      'Listen to {work} by {composer} in stunning, lossless sound quality. Stream now on IDAGIO',
  },
  title: {
    id: 'work.meta.title-2',
    defaultMessage: '{work} by {composer}',
  },
});

import type { Work as WorkShape, Recording, Facet, FilterParams } from '../shapes/types';
import type { LoadRecordingsByFilter } from '../actions/recording';
import type { Connector } from 'react-redux'; // eslint-disable-line
import shareableEntity from '../hoc/shareableEntity';
import { buildWorkSchemaWithRecordings } from '../lib/googleSchema';

// TODO type params and location
type OwnProps = {
  params: Object,
  location: Object,
  showShareModal: Function,
};

type MapStateToProps = {
  work: WorkShape,
  currentCursor: ?string,
  nextCursor: ?string,
  prevCursor: ?string,
  location: Object,

  activeFacets: Array<Facet>,
  recordings: Array<Recording>,

  filterParams: FilterParams,
} & OwnProps;

type DispatchProps = { loadRecordingsByFilter: LoadRecordingsByFilter };

type Props = MapStateToProps & DispatchProps;

class Work extends React.PureComponent<Props> {
  static onDataFetch(store, params, location, intl) {
    const state = store.getState();
    const work = selectWork(state, params.id);

    if (!work) {
      Promise.reject(new ApiError(404, 'Not found', 'Work not found'));
      return;
    }

    const description = intl.formatMessage(messages.metaDescription, {
      work: work.title,
      composer: work.composer.name,
    });

    const filterParams = selectRecordingsFilterParams(location.query, params.id, 'works');
    const recordings = selectFilteredAndSortedRecordings(state, filterParams);

    store.dispatch(
      clientActions.setHelmet({
        title:
          META_TITLE_PREFIX +
          intl.formatMessage(messages.title, {
            work: work.title,
            composer: work.composer.name,
          }),
        description,
        canonicalRoute: `/works/${work.id}`,
        ogType: 'music.song',
        googleSchema: buildWorkSchemaWithRecordings({ ...work, description, recordings }, intl),
        currentCursor: params.cursor,
        nextCursor: selectNextFilteredRecordingsCursor(state, filterParams),
        prevCursor: selectPreviousFilteredRecordingsCursor(state, filterParams),
      })
    );
  }

  renderHeader(work) {
    return (
      <div className={styles.header}>
        <div className={styles.info}>
          <ComposerList
            authors={work.authors}
            hyperlinks
            linkClassNames="work-header__name-link"
            className={styles.composerList}
          />
          <h1 className={`${styles.name} fz--beta`}>
            <TitleWithPopularTitle {...work} />
          </h1>
          <AuthorList authors={work.authors} className={styles.authorList} />
          <LiveCompareInvitationOverlay workId={work.id} />
        </div>
      </div>
    );
  }

  renderRecordingsWithFilter() {
    const { work, location, params, filterParams, activeFacets, recordings } = this.props;

    // Our data model allows for works without genres
    const facetOrder = work.genre
      ? WORKS_PAGE_FACET_FILTER_ORDERS[work.genre.id]
      : DEFAULT_WORKS_PAGE_FACET_FILTER_ORDER;

    return (
      <FilteredRecordingList
        contextType="work"
        contextFacet="Work"
        contextId={work.id.toString()}
        facetOrder={facetOrder}
        location={location}
        params={params}
        ignoredFacets={['works']}
        filterParams={filterParams}
        hideWorkTitle
        activeFacets={activeFacets}
        recordings={recordings}
      />
    );
  }

  render() {
    const { work } = this.props;
    return (
      <div className="u-page-container">
        {__CAPACITOR__ && (
          <CapacitorHeaderBar
            showShareModal={this.props.showShareModal}
            title={work.title}
            onlyShowTitleOnScroll
          />
        )}
        {this.renderHeader(work)}
        <div className={styles.component}>{this.renderRecordingsWithFilter()}</div>
      </div>
    );
  }
}

function fetchData(store, { id }, { query }) {
  const filterParams = selectRecordingsFilterParams(query, id, 'works');
  return [
    store.dispatch(workActions.loadWork(id)),
    store.dispatch(recordingActions.loadRecordingsByFilter(filterParams)),
    store.dispatch(loadTopRecordingFacets(filterParams)),
  ];
}

function mapStateToProps(state: Object, ownProps: OwnProps): MapStateToProps | {} {
  const { params, location } = ownProps;
  const { cursor, id } = params;
  const work = selectWork(state, id);
  const filterParams = selectRecordingsFilterParams(location.query, id, 'works');
  const selectNextCursor = selectNextFilteredRecordingsCursor;
  const selectPrevCursor = selectPreviousFilteredRecordingsCursor;
  return {
    work,
    currentCursor: cursor,
    nextCursor: selectNextCursor(state, filterParams),
    prevCursor: selectPrevCursor(state, filterParams),
    location,
    activeFacets: selectActiveRecordingFacets(state, filterParams, 'works'),
    filterParams,
    recordings: selectFilteredAndSortedRecordings(state, filterParams),
  };
}

const dispatchProps: DispatchProps = {
  loadRecordingsByFilter: recordingActions.loadRecordingsByFilter,
};

const connector: Connector<OwnProps, Props> = connect(mapStateToProps, dispatchProps);

function getShareTrackingContext(props) {
  return {
    sharedContentType: 'Work',
    sharedContentId: props.work.id,
    contextType: 'Work',
    contentId: props.work.id,
  };
}

export default compose(
  dataComponent(fetchData),
  connector,
  shareableEntity(ENTITY_TYPE_WORK, getShareTrackingContext)
)(Work);
